// Built-in YAML analyzer (ARCHITECTURE.md §6.3, §6.8).
//
// Uses the `yaml` npm package to parse documents into a CST/AST without any
// execution. `$ref` strings (JSON Schema / OpenAPI references) become `imports`
// edges, and top-level mapping keys become exported symbols. tree-sitter is
// unnecessary here — `$ref` is a simple string pattern (§6.3).
//
// Parser-specific types stay internal; the output is the language-independent
// FileAnalysisResult IR. All edges are deterministic (confidence 1.0).

import { isMap, isScalar, LineCounter, parseDocument, visit } from "yaml";

import type { GraphEdge } from "../../types.js";
import type {
  AnalyzerCapability,
  FileAnalysisResult,
  LanguageAnalyzer,
  SymbolInfo,
} from "../types.js";

const FILE_EXTENSIONS = [".yaml", ".yml"] as const;

const CAPABILITIES: AnalyzerCapability[] = ["symbols", "imports"];

export class YamlAnalyzer implements LanguageAnalyzer {
  readonly language = "yaml";
  readonly capabilities = CAPABILITIES;
  readonly fileExtensions = FILE_EXTENSIONS;

  async analyze(filePath: string, content: string): Promise<FileAnalysisResult> {
    const start = performance.now();

    const symbols: SymbolInfo[] = [];
    const edges: GraphEdge[] = [];

    const lineCounter = new LineCounter();
    const doc = parseDocument(content, { lineCounter });

    const lineAt = (offset: number | undefined): number =>
      offset === undefined ? 1 : lineCounter.linePos(offset).line;

    // Top-level mapping keys → exported symbols.
    if (isMap(doc.contents)) {
      for (const item of doc.contents.items) {
        if (isScalar(item.key) && typeof item.key.value === "string") {
          const startLine = lineAt(item.key.range?.[0]);
          // End of the value (if any) bounds the key's scope; otherwise the key.
          const endLine = lineAt(
            item.value && typeof item.value === "object" && "range" in item.value
              ? (item.value.range?.[2] ?? item.key.range?.[2])
              : item.key.range?.[2],
          );
          symbols.push({
            name: item.key.value,
            kind: "variable",
            exported: true,
            line: startLine,
            endLine: endLine < startLine ? startLine : endLine,
            symbolId: `${filePath}#${item.key.value}`,
          });
        }
      }
    }

    // $ref strings anywhere in the document → imports edges.
    visit(doc, {
      Pair(_key, pair) {
        const key = pair.key;
        const value = pair.value;
        if (
          isScalar(key) &&
          key.value === "$ref" &&
          isScalar(value) &&
          typeof value.value === "string"
        ) {
          const ref = value.value;
          edges.push({
            from: filePath,
            to: ref,
            kind: "imports",
            classification: "deterministic",
            evidence: [
              {
                type: "static_import",
                detail: `$ref '${ref}'`,
                line: lineAt(value.range?.[0]),
                confidence: 1,
              },
            ],
          });
        }
      },
    });

    return {
      filePath,
      language: this.language,
      symbols,
      edges,
      metadata: {
        parserBackend: "yaml",
        parseTimeMs: performance.now() - start,
        lineCount: lineCounter.lineStarts.length,
      },
    };
  }
}
